import React, { useState, useEffect } from 'react';

import { Save } from 'lucide-react';
import { voucherService } from '../../services/voucherService';
import { searchService } from '../../services/searchService';
import Navigation from '../../components/Navigation';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';



import realtimeService from '../../services/realtimeService';

const Marketplace = () => {
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [issuerSearch, setIssuerSearch] = useState('');
  const [showAvailabilityDropdown, setShowAvailabilityDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // New state for saved searches and history
  const [savedSearches, setSavedSearches] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showSaveSearchModal, setShowSaveSearchModal] = useState(false);
  const [newSearchName, setNewSearchName] = useState('');

  // Availability options mapping
  const availabilityOptions = [
    { value: 'all', label: 'Sve opcije' },
    { value: 'for_sale', label: 'Nudi se za prodaju' },
    { value: 'for_exchange', label: 'Nudi se za razmenu' },
    { value: 'wanted_purchase', label: 'Traži se za otkup' },
    { value: 'wanted_exchange', label: 'Traži se za razmenu' },
    { value: 'not_available', label: 'Trenutno nije u ponudi' }
  ];

  useEffect(() => {
    loadCategories();
    loadVouchers();
    if (user) {
      loadSavedSearches();
      loadSearchHistory();
    }
  }, [user]);

  // Real-time voucher subscription
  useEffect(() => {
    // Subscribe to voucher changes
    const voucherChannel = realtimeService?.subscribeToVouchers({
      onInsert: (newVoucher) => {
        // Add new voucher to list if it matches current filters
        setVouchers((prev) => {
          // Check if voucher matches current filters
          const matchesFilters = 
            (selectedAvailability === 'all' || newVoucher?.availability_type === selectedAvailability) &&
            (selectedCategory === 'all' || newVoucher?.category_id === selectedCategory) &&
            (!issuerSearch || newVoucher?.company_name?.toLowerCase()?.includes(issuerSearch?.toLowerCase())) &&
            (!searchTerm || newVoucher?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()));

          if (matchesFilters) {
            // Check if voucher already exists
            const exists = prev?.some(v => v?.id === newVoucher?.id);
            if (!exists) {
              return [newVoucher, ...prev];
            }
          }
          return prev;
        });
      },
      onUpdate: (updatedVoucher) => {
        setVouchers((prev) =>
          prev?.map((voucher) =>
            voucher?.id === updatedVoucher?.id ? { ...voucher, ...updatedVoucher } : voucher
          )
        );
      },
      onDelete: (deletedVoucher) => {
        setVouchers((prev) => prev?.filter((voucher) => voucher?.id !== deletedVoucher?.id));
      }
    });

    // Cleanup subscription on unmount
    return () => {
      realtimeService?.unsubscribe(voucherChannel);
    };
  }, [selectedAvailability, selectedCategory, issuerSearch, searchTerm]);

  // Load categories from database
  const loadCategories = async () => {
    try {
      const { data, error } = await supabase?.from('categories')?.select('*')?.eq('is_active', true)?.order('name');
      
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
    }
  };

  // Load saved searches
  const loadSavedSearches = async () => {
    try {
      const searches = await searchService?.getSavedSearches();
      setSavedSearches(searches || []);
    } catch (error) {
      console.error('Error loading saved searches:', error);
    }
  };

  // Load search history
  const loadSearchHistory = async () => {
    try {
      const history = await searchService?.getSearchHistory(10);
      setSearchHistory(history || []);
    } catch (error) {
      console.error('Error loading search history:', error);
    }
  };

  // Load vouchers with filters
  const loadVouchers = async () => {
    try {
      setLoading(true);
      const filters = {
        searchTerm: searchTerm || undefined,
        availabilityType: selectedAvailability !== 'all' ? selectedAvailability : undefined,
        categoryId: selectedCategory !== 'all' ? selectedCategory : undefined,
        issuer: issuerSearch || undefined
      };
      
      const data = await voucherService?.getWithFilters(filters);
      setVouchers(data || []);
      
      // Add to search history if user is logged in and any filter is active
      if (user && (searchTerm || selectedAvailability !== 'all' || selectedCategory !== 'all' || issuerSearch)) {
        await searchService?.addToHistory(filters, data?.length || 0);
        loadSearchHistory();
      }
    } catch (error) {
      console.error('Error loading vouchers:', error);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  // Save current search
  const handleSaveSearch = async () => {
    if (!newSearchName?.trim()) {
      alert('Molimo unesite naziv pretrage');
      return;
    }

    try {
      const filters = {
        searchTerm: searchTerm || undefined,
        availabilityType: selectedAvailability !== 'all' ? selectedAvailability : undefined,
        categoryId: selectedCategory !== 'all' ? selectedCategory : undefined,
        issuer: issuerSearch || undefined
      };

      await searchService?.saveSearch(newSearchName, filters);
      setNewSearchName('');
      setShowSaveSearchModal(false);
      loadSavedSearches();
      alert('Pretraga je uspešno sačuvana!');
    } catch (error) {
      console.error('Error saving search:', error);
      alert('Greška pri čuvanju pretrage');
    }
  };

  // Load saved search
  const handleLoadSavedSearch = (search) => {
    setSearchTerm(search?.search_term || '');
    setSelectedAvailability(search?.availability_type || 'all');
    setSelectedCategory(search?.category_id || 'all');
    setIssuerSearch(search?.issuer || '');
    setShowSavedSearches(false);
  };

  // Delete saved search
  const handleDeleteSavedSearch = async (searchId, event) => {
    event?.stopPropagation();
    if (confirm('Da li ste sigurni da želite da obrišete ovu sačuvanu pretragu?')) {
      try {
        await searchService?.deleteSavedSearch(searchId);
        loadSavedSearches();
      } catch (error) {
        console.error('Error deleting saved search:', error);
        alert('Greška pri brisanju pretrage');
      }
    }
  };

  // Load from history
  const handleLoadFromHistory = (historyItem) => {
    setSearchTerm(historyItem?.search_term || '');
    setSelectedAvailability(historyItem?.availability_type || 'all');
    setSelectedCategory(historyItem?.category_id || 'all');
    setIssuerSearch(historyItem?.issuer || '');
    setShowSearchHistory(false);
  };

  // Clear search history
  const handleClearHistory = async () => {
    if (confirm('Da li ste sigurni da želite da obrišete celu istoriju pretrage?')) {
      try {
        await searchService?.clearHistory();
        setSearchHistory([]);
      } catch (error) {
        console.error('Error clearing history:', error);
        alert('Greška pri brisanju istorije');
      }
    }
  };

  // Apply filters when any filter changes
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadVouchers();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedAvailability, selectedCategory, issuerSearch]);

  // Responsive iframe height based on viewport width
  const [iframeHeight, setIframeHeight] = useState('calc(100vh - 112px)');

  useEffect(() => {
    const updateIframeHeight = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setIframeHeight('calc(100vh - 84px)');
      } else if (width >= 768) {
        setIframeHeight('calc(100vh - 96px)');
      } else {
        setIframeHeight('calc(100vh - 112px)');
      }
    };

    updateIframeHeight();
    window.addEventListener('resize', updateIframeHeight);
    return () => window.removeEventListener('resize', updateIframeHeight);
  }, []);

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col">
      <Navigation />
      
      {/* ValueBook.app Embedded Content - Full height below navigation */}
      <div className="flex-1 w-full overflow-hidden">
        <iframe
          src="https://valuebook.app"
          title="ValueBook Application"
          className="w-full border-0 block"
          style={{ height: iframeHeight }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          loading="lazy"
        />
      </div>
    </div>
  );
};

export default Marketplace;